import { AxiosError } from 'axios';
import { type MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { KYCStatus } from '~/api/KYCStatus';
import { company } from '~/company/Company';
import { useModal } from '~/hooks/useModal';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';

import { mainStore, userStore } from '~/stores';

import { ResponseErrorCodes } from '../constants';
import type { OtpErrorBody } from '../types';

/** Current step in the authentication flow */
export type AuthStep = 'phone' | 'otp';

/**
 * State object for phone number input management.
 */
interface PhoneState {
  /** Current phone number value */
  value: string;
  /** Updates the phone number value */
  setValue: (value: string) => void;
  /** Whether the phone number is valid */
  isValid: boolean;
  /** Updates the phone validation status */
  setIsValid: (value: boolean) => void;
  /** Current error message or null */
  error: string | null;
}

/**
 * State object for terms agreement checkbox management.
 */
interface AgreementState {
  /** Whether the agreement checkbox is checked */
  checked: boolean;
  /** Updates the checkbox state */
  setChecked: (checked: boolean) => void;
  /** Whether there is a validation error */
  error: boolean;
  /** Whether the error notification is visible */
  errorVisible: boolean;
  /** Closes the error notification */
  handleErrorClose: () => void;
}

/**
 * State object for OTP code input management.
 */
interface OtpState {
  /** Current OTP code value */
  value: string;
  /** Updates the OTP code value */
  setValue: (value: string) => void;
  /** Whether the OTP code is complete and valid */
  isValid: boolean;
  /** Updates the OTP validation status */
  setIsValid: (value: boolean) => void;
}

/**
 * Return type for the useAuthFlow hook.
 */
interface UseAuthFlowReturn {
  /** Current authentication step */
  step: AuthStep;
  /** Phone input state and handlers */
  phone: PhoneState;
  /** Agreement checkbox state and handlers */
  agreement: AgreementState;
  /** OTP input state and handlers */
  otp: OtpState;
  /** Whether an async operation is in progress */
  isLoading: boolean;
  /** Whether the user account was previously deleted */
  isDeletedUser: boolean;
  /** Seconds until user can retry after max attempts reached */
  maxAttemptsThreshold: number;
  /** Mutable ref holding the Cloudflare Turnstile widget ID once rendered */
  captchaWidgetIdRef: MutableRefObject<string | undefined>;
  requestCode: () => void;
  /** Submits phone number and requests OTP code */
  handleSubmitPhone: () => void;
  /** Verifies the entered OTP code */
  handleSubmitOtp: () => Promise<void>;
  /** Returns to phone input step */
  handleChangePhone: () => void;
  /** Closes the authentication modal */
  handleClose: () => void;
}

/**
 * Hook that manages the complete authentication flow including phone verification and OTP.
 * Handles form validation, API calls, error handling, and navigation between steps.
 * @returns Authentication flow state and handler functions
 */
export const useAuthFlow = (): UseAuthFlowReturn => {
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const navigate = useNavigate();

  const [step, setStep] = useState<AuthStep>('phone');
  const [phoneNumber, setPhoneNumber] = useState(
    userStore.personalData.phone || '',
  );
  const [isPhoneValid, setIsPhoneValid] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [isOtpValid, setIsOtpValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletedUser, setIsDeletedUser] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [agreementError, setAgreementError] = useState(false);
  const [agreementErrorVisible, setAgreementErrorVisible] = useState(true);
  const [maxAttemptsThreshold, setMaxAttemptsThreshold] = useState(0);

  const captchaWidgetIdRef = useRef<string | undefined>(undefined);
  const phoneNumberRef = useRef(phoneNumber);

  useEffect(() => {
    phoneNumberRef.current = phoneNumber;
  }, [phoneNumber]);

  useEffect(() => {
    if (!userStore.isAuthorized) {
      return;
    }
    if (userStore.personalData.kycStatus !== KYCStatus.Verified) {
      navigate('/cabinet/personal-data');
    }
    closeModal();
  }, [userStore.isAuthorized, closeModal, navigate]);

  useEffect(() => {
    analyticsService.track(AnalyticsEvent.LOGIN_SCREEN_SHOWN);
    analyticsService.track(AnalyticsEvent.LOGIN_SCREEN_SHOWN_BI);
  }, []);

  const validatePhoneForm = (): boolean => {
    let isValid = true;

    if (!phoneNumber.trim()) {
      setPhoneError(t('errors:cantBeEmpty'));
      isValid = false;
    } else if (!isPhoneValid) {
      setPhoneError(t('phrases:enterValidPhoneNumber'));
      isValid = false;
    } else {
      setPhoneError(null);
    }

    if (!agreementChecked) {
      setAgreementError(true);
      setAgreementErrorVisible(true);
      isValid = false;
    } else {
      setAgreementError(false);
    }

    return isValid;
  };

  const requestCode = async () => {
    setIsLoading(true);
    try {
      const captchaToken =
        company.isEnabledCaptcha && !mainStore.isRN
          ? (captchaWidgetIdRef.current &&
              window.turnstile?.getResponse(captchaWidgetIdRef.current)) ||
            ''
          : company.envVariables.REACT_APP_PREDEFINED_CAPTCHA_TOKEN;
      const res = await userStore.requestAuthCode(
        phoneNumberRef.current,
        captchaToken,
      );
      if ('isDeleted' in res && res.isDeleted) {
        setIsDeletedUser(true);
      }
      setStep('otp');
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 429 && !error.response?.data?.message) {
          mainStore.pushAlert(
            'error',
            `${t('errors:unknown')}. ${t('phrases:supportLink')}`,
          );
        } else if (error.response?.data?.message) {
          mainStore.pushAlert('error', String(error.response.data.message));
        } else {
          mainStore.pushAlert('error', t('errors:unknown'));
        }
      } else {
        mainStore.pushAlert('error', t('errors:unknown'));
      }
    }
    setIsLoading(false);
  };

  const handleSubmitPhone = () => {
    const isValid = validatePhoneForm();

    if (!isValid) {
      return;
    }

    const widgetId = captchaWidgetIdRef.current;
    if (widgetId && window.turnstile) {
      window.turnstile.reset(widgetId);
      window.turnstile.execute(widgetId);
    } else {
      requestCode();
    }
  };

  const handleSubmitOtp = async (): Promise<void> => {
    if (!phoneNumber || !otpCode) {
      return;
    }
    setIsLoading(true);
    try {
      const data = await userStore.verifyAuthCode(
        phoneNumber.replace('+', ''),
        otpCode,
        Boolean(isDeletedUser),
      );
      if ('customer' in data && !data.customer.email && data.previousEmail) {
        mainStore.pushAlert('error', t('errors:emailIsAlreadyUse'));
      }
    } catch (error) {
      const errorBody: OtpErrorBody =
        (error as AxiosError<OtpErrorBody>)?.response?.data ||
        ({} as OtpErrorBody);
      if (errorBody.code) {
        switch (errorBody.code) {
          case ResponseErrorCodes.InvalidOtp:
            mainStore.pushAlert('error', t('errors:invalidCodeError'));
            break;
          case ResponseErrorCodes.TryOtpLimitIsReached:
            mainStore.pushAlert(
              'error',
              t('errors:tryOtpLimitIsReached', {
                timer: errorBody.timeout ? ` in ${errorBody.timeout}s` : '',
              }),
            );
            setMaxAttemptsThreshold(errorBody.timeout ?? 0);
            setStep('phone');
            setOtpCode('');
            break;
          default:
            mainStore.pushAlert('error', t('errors:otpCodeError'));
        }
      } else {
        mainStore.pushAlert('error', t('errors:otpCodeError'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePhone = () => {
    setStep('phone');
    setOtpCode('');
    setIsOtpValid(false);
    if (captchaWidgetIdRef.current) {
      window.turnstile?.reset(captchaWidgetIdRef.current);
    }
  };

  const handleClose = useCallback(() => {
    closeModal();
  }, [closeModal]);

  const handlePhoneValidation = (flag: boolean) => {
    setIsPhoneValid(flag);

    if (!flag) {
      setOtpCode('');
      setIsOtpValid(false);
      setMaxAttemptsThreshold(0);
    } else {
      analyticsService.track(AnalyticsEvent.LOGIN_PHONE_ENTERED, {});
    }
  };

  const handleAgreementChange = (checked: boolean) => {
    setAgreementChecked(checked);
    if (checked) {
      setAgreementError(false);
    }
  };

  const handleAgreementErrorClose = () => {
    setAgreementErrorVisible(false);
  };

  return {
    step,
    phone: {
      value: phoneNumber,
      setValue: setPhoneNumber,
      isValid: isPhoneValid,
      setIsValid: handlePhoneValidation,
      error: phoneError,
    },
    agreement: {
      checked: agreementChecked,
      setChecked: handleAgreementChange,
      error: agreementError,
      errorVisible: agreementErrorVisible,
      handleErrorClose: handleAgreementErrorClose,
    },
    otp: {
      value: otpCode,
      setValue: setOtpCode,
      isValid: isOtpValid,
      setIsValid: setIsOtpValid,
    },
    isLoading,
    isDeletedUser,
    maxAttemptsThreshold,
    captchaWidgetIdRef,
    requestCode,
    handleSubmitPhone,
    handleSubmitOtp,
    handleChangePhone,
    handleClose,
  };
};
